import { createContext, useMemo } from 'react'
import groupBy from 'lodash/groupBy'
import { useTeam } from 'app/frontend/base/config/context/team'
import RoleBlock from './RoleBlock'
import {
  useTeamCharacterDetails,
  useTeamUsers,
} from 'app/frontend/base/config/queries'
import { useGroupedTeamMembers } from '../hooks'
import RosterStats from './RosterStats'
import { useBreakpoint } from 'app/frontend/base/helpers/hooks'

type Props = {
  members: wowaudit.TeamMembership[]
}

export const RosterSetupContext = createContext<{
  mains: wowaudit.TeamMembership[]
  alts: wowaudit.TeamMembership[]
  membersWithOffspecs: {
    member: wowaudit.TeamMembership
    offspecRole: string
  }[]
  mainsByRole: Record<string, wowaudit.TeamMembership[]>
  altsByRole: Record<string, wowaudit.TeamMembership[]>
  offSpecsByRole: Record<
    string,
    { member: wowaudit.TeamMembership; offspecRole: string }[]
  >
  charactersById?: Record<string, wowaudit.CharacterDetails>
  mainByUser?: Record<string, wowaudit.TeamMembership>
  usersById?: Record<string, wowaudit.UserReference>
  userIdsWithDuplicateMains: number[]
  sortedMembers: wowaudit.TeamMembership[]
}>({
  mains: [],
  alts: [],
  membersWithOffspecs: [],
  mainsByRole: {},
  altsByRole: {},
  offSpecsByRole: {},
  charactersById: {},
  mainByUser: {},
  userIdsWithDuplicateMains: [],
  sortedMembers: [],
})

const RosterSetup = ({ members }: Props) => {
  const { team } = useTeam()
  const breakpoint = useBreakpoint()

  const sortedMembers = useMemo(() => {
    return members.sort((a, b) => {
      return `${a.characterReference.characterClass}-${a.characterReference.name}`.localeCompare(
        `${b.characterReference.characterClass}-${b.characterReference.name}`,
      )
    })
  }, [members])

  const mains = useMemo(() => {
    return sortedMembers?.filter((m) => !m.teamRank.for_alts)
  }, [sortedMembers])

  const mainsByRole = useMemo(() => {
    return groupBy(mains, (m) => m.role)
  }, [mains])

  const alts = useMemo(() => {
    return sortedMembers?.filter((m) => m.teamRank.for_alts)
  }, [sortedMembers])

  const altsByRole = useMemo(() => {
    return groupBy(alts, (m) => m.role)
  }, [alts])

  const membersWithOffspecs = useMemo(() => {
    return (
      sortedMembers?.flatMap((member) =>
        member.offspecDetails
          .filter((offspec) => offspec.role && offspec.role !== member.role)
          .map((offspec) => ({
            member,
            offspecRole: offspec.role,
          })),
      ) ?? []
    )
  }, [sortedMembers])

  const offSpecsByRole = useMemo(() => {
    return groupBy(membersWithOffspecs, (item) => item.offspecRole)
  }, [membersWithOffspecs])

  const { mainByUser, userIdsWithDuplicateMains } = useGroupedTeamMembers()

  const { data: memberDetails } = useTeamCharacterDetails(
    team.id,
    sortedMembers?.map((member) => member.characterReference.id),
  )

  const charactersById = useMemo(
    () =>
      memberDetails?.reduce((acc, character) => {
        acc[character.id.toString()] = character
        return acc
      }, {} as Record<string, wowaudit.CharacterDetails>),
    [memberDetails],
  )

  const { data: teamUsers } = useTeamUsers(team.id)
  const usersById = useMemo(
    () =>
      teamUsers?.reduce((acc, user) => {
        acc[user.id.toString()] = user
        return acc
      }, {} as Record<string, wowaudit.UserReference>),
    [teamUsers],
  )

  return (
    <RosterSetupContext.Provider
      value={{
        mains,
        mainsByRole,
        alts,
        altsByRole,
        membersWithOffspecs,
        offSpecsByRole,
        charactersById,
        mainByUser,
        usersById,
        userIdsWithDuplicateMains: userIdsWithDuplicateMains ?? [],
        sortedMembers,
      }}
    >
      <div className='flex gap-4 flex-col xl:flex-row'>
        <div className='flex-1'>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
            {['sm', 'md'].includes(breakpoint) ? (
              <>
                <div className='flex flex-col gap-3'>
                  <RoleBlock className='grow' role={'tank'} />
                  <RoleBlock className='grow' role={'ranged'} />
                </div>
                <div className='flex flex-col gap-3'>
                  <RoleBlock className='grow' role={'heal'} />
                  <RoleBlock className='grow' role={'melee'} />
                </div>
              </>
            ) : (
              <>
                <div className='flex flex-col gap-3'>
                  <RoleBlock className='grow' role={'tank'} />
                  <RoleBlock className='grow' role={'heal'} />
                </div>
                <RoleBlock role={'melee'} />
                <RoleBlock role={'ranged'} />
              </>
            )}
          </div>
        </div>

        <RosterStats />
      </div>
    </RosterSetupContext.Provider>
  )
}

export default RosterSetup
